import "./input-select.scss";

import { Image } from "react-bootstrap";
import { CrossIcon, LeftIcon } from "src/assets";
import i18n from "src/i18n";
import { InputSelectOption, InputSelectProps, typedMemo } from "src/types";
import { formatLabel } from "src/utils";

import { useInputSelect } from "./input-select.hook";

export const InputSelect = typedMemo((props: InputSelectProps) => {
  const {
    name = "",
    value: selectedOptionId,
    validationSchema,
    disabled,
    label,
    error,
    placeholder = i18n.t("commons.select"),
    options,
    classes = "",
    classesLabel = "input-select--label",
    classesMenu = "input-select--menu input-select--menu--overlay input-select--z-index",
  } = props;

  const {
    expanded,
    allClassesArrow,
    allClassesInput,
    allClassesHead,
    allClassesField,
    allClassesError,
    searchTerm,
    filteredOptions,
    dropdownRef,
    classesIcon,
    searchInputRef,
    t,
    toggleMenu,
    updateTouched,
    updateOption,
    onKeyDownHead,
    onSearchChange,
    onKeyDownOption,
  } = useInputSelect(props);

  ////////////////////////////////////////////////////////////////////////////////////////////////////

  const Label = (
    <label htmlFor={name} className={`${classesLabel} mb-md-2 mb-1`}>
      {formatLabel(validationSchema, name, label)}
    </label>
  );

  const Input = (
    <div className={allClassesInput}>
      {options?.find(({ id }) => id === selectedOptionId)?.value || placeholder}
    </div>
  );

  const SearchInput = (
    <input
      id={`input-search-${name}`}
      ref={searchInputRef}
      type="text"
      className="input-select--search"
      placeholder={placeholder}
      value={searchTerm}
      onChange={onSearchChange}
      disabled={disabled}
    />
  );

  const Arrow = (
    <Image src={LeftIcon} width={15} height={15} className={allClassesArrow} />
  );

  const Cross = (
    <div
      onClick={() => {
        updateOption();
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }}
      className={classesIcon}
    >
      {" "}
      <Image src={CrossIcon} width={15} height={15} />
    </div>
  );

  const Head = (
    <div
      id={name}
      className={allClassesHead}
      onClick={!disabled ? toggleMenu : undefined}
      onKeyDown={onKeyDownHead}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={expanded}
      aria-controls={`${name}-listbox`}
      aria-disabled={!!disabled}
    >
      {selectedOptionId ? Input : SearchInput}
      {selectedOptionId ? Cross : Arrow}
    </div>
  );

  const Option = (option: InputSelectOption, index: number) => (
    <div
      key={index}
      className="input-select--option"
      onKeyDown={(e) => {
        onKeyDownOption(e, option);
      }}
    >
      <div
        className="input-select--option-value"
        onClick={() => updateOption(option)}
        tabIndex={0}
      >
        {option.value}
      </div>
      <div className="input-select--separator" />
    </div>
  );

  const Error = <p className={allClassesError}>{`${error} `}</p>;

  ////////////////////////////////////////////////////////////////////////////////////////////////////

  const OptionsList = (
    <div
      className={"input-select--options input-select--options--scroll"}
      tabIndex={-1}
    >
      {filteredOptions?.map(Option)}
      {filteredOptions?.length === 0 && (
        <div className="col-12 d-flex justify-content-center mb-2">
          {t("commons.no_results_found")}
        </div>
      )}
    </div>
  );

  const Menu = (
    <div
      className={`${classesMenu} ${
        props.error && props.touched ? "input-select--menu--overlay-error" : ""
      }`}
    >
      {OptionsList}
    </div>
  );

  const Field = (
    <div ref={dropdownRef} className={allClassesField} onClick={updateTouched}>
      {Head}
      {expanded && !disabled && Menu}
    </div>
  );

  return (
    <main id={`input-${name}`} className={classes}>
      <div className="d-flex flex-column">
        {Label}
        {Field}
        {Error}
      </div>
    </main>
  );
});
